import en from './messages/en.json';
import deJson from './messages/de.json';

/** Supported UI locales (spec §6: English + German at launch). */
export type Locale = 'en' | 'de';
export const locales: readonly Locale[] = ['en', 'de'] as const;

/**
 * The typed message-key union, derived from the English bundle.
 * Unknown keys are a compile error everywhere `t`/`useT` is used.
 */
export type MessageKey = keyof typeof en;

export type MessageParams = Record<string, string | number>;

/** German must contain every English key — missing keys fail `tsc --noEmit`. */
const de = deJson satisfies Record<MessageKey, string>;

export const bundles: Record<Locale, Record<MessageKey, string>> = { en, de };

/**
 * Simple ICU-style interpolation: replaces `{name}` placeholders from params.
 * Unknown placeholders are left verbatim so missing params are visible in QA.
 */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export type Translator = (key: MessageKey, params?: MessageParams) => string;

/**
 * Build a translator over arbitrary (possibly partial) bundles.
 * Lookup order: requested locale → English → the key itself.
 * Exposed mainly for tests and future locale additions; app code uses `t`/`useT`.
 */
export function createTranslator(
  allBundles: Partial<Record<Locale, Partial<Record<MessageKey, string>>>>,
): (locale: Locale, key: MessageKey, params?: MessageParams) => string {
  return (locale, key, params) => {
    const template = allBundles[locale]?.[key] ?? allBundles.en?.[key] ?? key;
    return interpolate(template, params);
  };
}

/**
 * Framework-free translation for server use (push templates, emails).
 * `t('de', 'ec2.quest.meta.startsAt', { time: '14:00' })`
 */
export const t = createTranslator(bundles);
